import { AlertCircle, Check, ChevronLeft, LoaderCircle, X } from 'lucide-react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}) {
  return (
    <button className={clsx('button', `button--${variant}`, `button--${size}`, className)} disabled={disabled || loading} {...props}>
      {loading ? <LoaderCircle size={18} className="spin" aria-hidden="true" /> : null}
      {children}
    </button>
  )
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={clsx('card', className)}>{children}</section>
}

export function Field({ label, hint, error, children, required }: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
  required?: boolean
}) {
  return (
    <label className="field">
      <span className="field__label">{label}{required ? <span aria-hidden="true"> *</span> : null}</span>
      {children}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error"><AlertCircle size={14} aria-hidden="true" />{error}</span> : null}
    </label>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx('input', props.className)} {...props} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={clsx('input', 'select', props.className)} {...props} />
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx('input', 'textarea', props.className)} {...props} />
}

export function PageHeader({ eyebrow, title, description, backTo, action }: {
  eyebrow?: string
  title: string
  description?: string
  backTo?: string
  action?: ReactNode
}) {
  return (
    <header className="page-header">
      <div className="page-header__main">
        {backTo ? <Link to={backTo} className="icon-button page-header__back" aria-label="Go back"><ChevronLeft /></Link> : null}
        <div>
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          <h1>{title}</h1>
          {description ? <p>{description}</p> : null}
        </div>
      </div>
      {action ? <div className="page-header__action">{action}</div> : null}
    </header>
  )
}

export function WizardProgress({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div className="wizard-progress" aria-label={`Step ${current} of ${labels.length}: ${labels[current - 1]}`}>
      <div className="wizard-progress__line"><span style={{ width: `${((current - 1) / (labels.length - 1)) * 100}%` }} /></div>
      <div className="wizard-progress__labels">
        {labels.map((label, index) => (
          <span key={label} className={clsx(index + 1 <= current && 'is-active')}>
            <i>{index + 1 < current ? <Check size={12} /> : index + 1}</i>
            <b>{label}</b>
          </span>
        ))}
      </div>
    </div>
  )
}

export function Notice({ tone = 'info', title, children, onDismiss }: {
  tone?: 'info' | 'success' | 'warning' | 'danger'
  title?: string
  children: ReactNode
  onDismiss?: () => void
}) {
  return (
    <div className={clsx('notice', `notice--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <AlertCircle size={19} aria-hidden="true" />
      <div>{title ? <strong>{title}</strong> : null}<div>{children}</div></div>
      {onDismiss ? <button className="icon-button" onClick={onDismiss} aria-label="Dismiss"><X size={17} /></button> : null}
    </div>
  )
}

export function EmptyState({ icon, title, description, action }: { icon: ReactNode; title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><div className="empty-state__icon">{icon}</div><h2>{title}</h2><p>{description}</p>{action}</div>
}

export function formatKcal(value: number): string {
  return `${Math.round(value).toLocaleString()} kcal`
}

export function formatMacro(value: number): string {
  return `${Math.round(value)} g`
}
